import {FC, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {api, ApiError} from '../../services/api'
import {useToast} from '../../components/Toast'
import {Spinner} from '../../components/Spinner'
import {ACCENT, INK, MUTED, money} from './ui'

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 13px', border: '1px solid #e5e7eb', borderRadius: 9,
  fontSize: 15, background: '#fff', boxSizing: 'border-box', outline: 'none',
}
const Label: FC<{children: React.ReactNode}> = ({children}) => (
  <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6}}>{children}</label>
)

/**
 * The cleaner logs an apartment-cleaning visit. At the base rate it lands on her
 * tab straight away; above it, a written reason is required and it waits for the
 * admin's approval.
 */
const CleanerLogVisitPage: FC = () => {
  const toast = useToast()
  const navigate = useNavigate()
  const today = new Date().toISOString().slice(0, 10)

  const [baseRate, setBaseRate] = useState('45.00')
  const [occurredOn, setOccurredOn] = useState(today)
  const [amount, setAmount] = useState('45.00')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    api.cleanerTab().then((t) => {
      setBaseRate(t.baseRate)
      setAmount(t.baseRate)
    }).catch(() => {})
  }, [])

  const aboveBase = parseFloat(amount || '0') > parseFloat(baseRate || '0')

  const submit = async () => {
    if (!(parseFloat(amount || '0') > 0)) return toast.error('Enter a valid amount.')
    if (aboveBase && !note.trim()) return toast.error('Please explain why this visit is above the base rate.')
    setSaving(true)
    try {
      const e = await api.logCleaningVisit({amount, occurredOn, note: note.trim() || undefined})
      if (e.status === 'PENDING_APPROVAL') {
        toast.success('Visit submitted — waiting for the admin to approve the extra amount.')
      } else {
        toast.success(`Visit logged. ${money(e.amount)} added to your tab.`)
      }
      navigate('/housekeeping/earnings')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not log the visit')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', border: '1px solid #eef0f4', padding: '24px 26px', maxWidth: 680}}>
      <h2 style={{fontSize: 18, fontWeight: 800, color: INK, margin: 0}}>Log a cleaning visit</h2>
      <p style={{fontSize: 13.5, color: MUTED, margin: '4px 0 20px'}}>
        For an apartment cleaning (not a bed wash). The base rate is {money(baseRate)}. Charging more needs a short reason and the admin’s OK.
      </p>

      <div style={{display: 'grid', gap: 16}}>
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16}}>
          <div>
            <Label>Date</Label>
            <input style={inputStyle} type='date' max={today} value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} />
          </div>
          <div>
            <Label>Amount ($)</Label>
            <input style={inputStyle} type='number' min='0' step='0.01' value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
        </div>
        {aboveBase && (
          <div style={{fontSize: 12.5, color: '#b8770a', marginTop: -6}}>
            Above the {money(baseRate)} base rate — a reason is required and the admin must approve it.
          </div>
        )}
        <div>
          <Label>Notes{aboveBase ? ' (required)' : ' (optional)'}</Label>
          <textarea
            style={{...inputStyle, minHeight: 90, resize: 'vertical'}}
            placeholder='e.g. cleaned the ceiling, extra deep clean after move-out'
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>

      <div style={{marginTop: 22}}>
        <button
          onClick={submit}
          disabled={saving}
          style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 26px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: 8}}
        >
          {saving && <Spinner />} Log visit
        </button>
      </div>
    </div>
  )
}

export {CleanerLogVisitPage}
